import { useEffect, useState } from 'react';
import SimilarProducts from '../model/SimilarProductsModel';
import { Status } from '../utils/enums/status';
import UserModel, { userToString } from '../model/UserModel';
import {
  getDetailedSimilarProducts,
  getProductsQuartiles,
} from '../controller/productController';
import { ErrorStatus } from '../utils/enums/error';
import getIp from '../utils/userSignature/ip';
import getUserAgent from '../utils/userSignature/userAgent';
import getDimensions from '../utils/userSignature/dimensions';
import getTimeZone from '../utils/userSignature/timeZone';
import getLanguage from '../utils/userSignature/language';
import getColorDepth from '../utils/userSignature/colorDepth';
import getCanvas from '../utils/userSignature/canvas';
import ConnexionModel from '../model/ConnexionModel';
import hash from '../utils/hash/hash';
import { sendConnexion } from '../controller/userController';
import Header from '../component/Header';
import ProductHeader from '../component/Product';
import SimilarProductsComponent from '../component/SimilarProducts';
import Informations from '../component/Informations';
import Footer from '../component/Footer';
import Banner from '../component/Banner';
import Quartiles from '../model/Quartiles';
import { Location } from '../utils/enums/Location.enum';
import { Origin } from '../utils/enums/Origin.enum';

function ProductPage() {
  const [product, setProduct] = useState<SimilarProducts | undefined>(
    undefined,
  );
  const [quartiles, setQuartiles] = useState<Quartiles>({
    global_quartiles: [],
    sub_category_quartiles: [],
    product_category_quartiles: [],
  });
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [status, setStatus] = useState<Status>(Status.PENDING);
  const [user, setUser] = useState<UserModel | undefined>(undefined);
  const [bannerVisible, setBannerVisible] = useState<boolean>(true);
  const [isGlobal, setIsGlobal] = useState<boolean>(false);
  const [dynamicPrice, setDynamicPrice] = useState<number | string>(0);

  useEffect(() => {
    const queryParameters = new URLSearchParams(window.location.search);
    const location = queryParameters.get('location') || Location.DETAILS;

    const id = queryParameters.get('id');
    const originQuery = queryParameters.get('origin');
    setIsGlobal(
      queryParameters.get('global') !== null
        ? queryParameters.get('global') === 'true'
        : false,
    );
    setDynamicPrice(queryParameters.get('price') ?? 0);

    let origin: string;

    if (id === null) {
      setStatus(Status.NO_QUERY);
    } else {
      if (originQuery !== null) {
        const splitOrigin = originQuery.split('-');
        if (
          splitOrigin!.length > 1 &&
          Date.now() - parseInt(splitOrigin![1]) < 1000
        ) {
          origin = splitOrigin![0];
        } else {
          origin = 'share';
        }
      } else {
        origin = Origin.CLICK;
      }
      getDetailedSimilarProducts(id!!)
        .then((resp) => {
          if (resp === ErrorStatus.INTERNAL_ERROR) {
            setStatus(Status.ERROR);
          } else if (resp === ErrorStatus.NOT_FOUND) {
            setStatus(Status.NOT_FOUND);
          } else {
            setProduct(resp);
            setStatus(Status.FOUND);
            getIp().then((ip) => {
              setUser({
                ip: ip.ip,
                userAgent: getUserAgent(),
                dimensions: getDimensions(),
                timeZone: getTimeZone(),
                language: getLanguage(),
                colorDepth: getColorDepth(),
                canvas: getCanvas(),
              });
              const connexion: ConnexionModel = {
                user_id: hash(
                  userToString({
                    ip: ip.ip,
                    userAgent: getUserAgent(),
                    dimensions: getDimensions(),
                    timeZone: getTimeZone(),
                    language: getLanguage(),
                    colorDepth: getColorDepth(),
                    canvas: getCanvas(),
                  }),
                ),
                brand: resp.product_details.site,
                date: Math.floor(
                  new Date(Date.now()).getTime() / 1000,
                ).toString(),
                location: location,
                origin: origin,
              };
              sendConnexion(connexion);
            });
          }
        })
        .catch((e) => {
          setStatus(Status.NOT_FOUND);
        });
      getProductsQuartiles().then((resp) => {
        setQuartiles(resp);
      });
    }
  }, []);

  if (status === Status.PENDING) {
    return (
      <div
        role="status"
        className="h-screen flex justify-center items-center bg-white dark:bg-black"
      >
        <svg
          aria-hidden="true"
          className="w-12 h-12 text-blackOverlay animate-spin dark:text-whiteOverlay fill-primary"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span className="sr-only">Loading...</span>
      </div>
    );
  } else if (status === Status.ERROR) {
    return (
      <div>
        <div className="h-screen bg-white dark:bg-black text-black dark:text-white font-sans flex flex-col gap-16 justify-center items-center p-8">
          <h1 className="text-center md:text-6xl font-medium text-2xl">
            Nous sommes en maintenance, nous revenons bientôt !
          </h1>
          <img alt="logo" src="logo_tossee.png" className="w-28 md:w-auto" />
        </div>
      </div>
    );
  } else if (status === Status.NO_QUERY) {
    return (
      <div className="h-screen bg-white dark:bg-black text-black dark:text-white p-16 flex flex-col gap-6 justify-center text-center items-center">
        <h1 className="md:text-6xl font-medium text-2xl">
          Vous n'avez pas sélectionné de produit !
        </h1>
        <p className="italic md:text-xl font-light text-base">
          Afin d'obtenir le coût environnemental d'un vêtement, allez sur l'un
          des sites commercants évalué par Tossée et utilisez notre extension !
        </p>
        <img src="/logo_tossee.png" alt="logo" className="w-28 md:w-auto" />
      </div>
    );
  } else if (status === Status.NOT_FOUND || product === null) {
    return (
      <div className="h-screen bg-white dark:bg-black text-black dark:text-white font-sans flex flex-col gap-16 justify-center items-center p-8">
        <p className="text-center md:text-2xl text-xl">
          Désolé, nous ne trouvons pas le produit que vous cherchez !
        </p>
        <img alt="logo" src="logo_tossee.png" className="w-28 md:w-auto" />
      </div>
    );
  } else {
    return (
      <div
        className={
          'h-screen bg-white dark:bg-black text-black dark:text-white font-sans overflow-hidden overflow-y-scroll xl:overflow-hidden'
        }
      >
        {isVisible && (
          <div
            onClick={() => setIsVisible(false)}
            className="absolute w-full h-full bg-blackLight top-0 left-0 z-40"
          ></div>
        )}
        {bannerVisible && <Banner setBannerIsVisible={setBannerVisible} />}
        <Header
          isVisible={isVisible}
          setIsVisible={setIsVisible}
          id={product!.product_details._id}
        />
        <div
          className={`flex flex-col xl:grid xl:grid-rows-2 xl:grid-cols-2 xl:grid-flow-col grid-flow-row ${
            bannerVisible ? 'xl:h-[79%]' : 'xl:h-[88%]'
          } p-3 gap-4`}
        >
          <ProductHeader
            url_image={product!.product_details.url_image}
            url_product={product!.product_details.url_product}
            name={product!.product_details.name}
            brand={product!.product_details.site}
            category={product!.product_details.sub_category}
            price={parseFloat(product!.product_details.price)}
            score={product!.product_details.impact_global_ecs}
            quartiles={quartiles}
            isGlobal={isGlobal}
            setIsGlobal={setIsGlobal}
            dynamicPrice={dynamicPrice}
          />
          <SimilarProductsComponent
            products={product!.similar_products}
            quartiles={quartiles}
            isGlobal={isGlobal}
            category={product!.product_details.sub_category}
          />
          <Informations
            user={user}
            product={product!.product_details}
            quartiles={quartiles}
            isGlobal={isGlobal}
          />
        </div>
        <Footer />
      </div>
    );
  }
}

export default ProductPage;
